/* Shopify Performance Layer 1.0.0 — dependency-free, no service worker.
 * Intended for Solago / Focal 8.11.4 and Solarhandel24 / Athens 1.9.2.
 * Uses standard links, not private theme APIs. Configure BEFORE loading.
 * Prefetch is a browser hint, not a guarantee of a request or cache reuse.
 */
(() => {
  'use strict';
  if (window.ShopifyPerformanceLayer) return; // Duplicate installation guard.
  const script = document.currentScript;
  const input = window.ShopifyPerformanceConfig || {};
  const defaults = {
    enabled: true,
    rootUrl: window.Shopify?.routes?.root || '/',
    hoverDelay: 150,
    maxPrefetches: 8, // Total unique hints per document, including viewport.
    speculationRules: true,
    viewport: false,
    viewportSelector: 'a[data-performance-viewport]',
    viewportDelay: 800,
    maxViewportPrefetches: 2,
    allowUnknownConnection: true,
    nonce: script?.nonce || '',
    debug: false
  };
  const config = Object.assign({}, defaults, input);
  for (const [key, min, max] of [
    ['hoverDelay', 80, 2000], ['maxPrefetches', 0, 20],
    ['viewportDelay', 300, 10000], ['maxViewportPrefetches', 0, 4]
  ]) {
    const n = config[key];
    config[key] = typeof n === 'number' && Number.isFinite(n)
      ? Math.min(max, Math.max(min, Math.floor(n))) : defaults[key];
  }
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const hints = new Set();
  const seen = new Set();
  const cleanups = [];
  const viewportTimers = new Map();
  let viewportCount = 0;
  let hoverTimer;
  let hoverLink;
  let observer;
  let mutations;
  let scanTimer;
  let started = false;
  let stopped = false;
  let destroyed = false;
  let root;
  try {
    const url = new URL(config.rootUrl, location.origin);
    if (url.origin !== location.origin) return;
    root = url.pathname.replace(/\/$/, '') + '/';
  } catch (_) { return; }

  const supportsRules = config.speculationRules === true &&
    typeof HTMLScriptElement !== 'undefined' &&
    typeof HTMLScriptElement.supports === 'function' &&
    HTMLScriptElement.supports('speculationrules');
  const probe = document.createElement('link');
  const supportsPrefetch = !!probe.relList?.supports?.('prefetch');
  const mechanism = supportsRules ? 'speculationrules' : supportsPrefetch ? 'link-prefetch' : 'none';

  function localPath(path) {
    if (path === root.slice(0, -1)) return '/';
    return path.startsWith(root) ? '/' + path.slice(root.length) : null;
  }

  // Strict positive list: excludes cart, account, checkout, search (all methods),
  // apps, redirects, assets, APIs, locale switchers, and unknown endpoints.
  function allowedPath(path) {
    return path === '/' ||
      /^\/products\/[^/.;]+\/?$/.test(path) ||
      /^\/collections(?:\/[^/.;]+(?:\/products\/[^/.;]+)?)?\/?$/.test(path) ||
      /^\/pages\/[^/.;]+\/?$/.test(path) ||
      /^\/blogs\/[^/.;]+(?:\/[^/.;]+)?\/?$/.test(path);
  }

  function networkOK(viewport = false) {
    if (navigator.onLine === false || connection?.saveData) return false;
    const type = connection?.effectiveType;
    if (type && /^(slow-2g|2g|3g)$/.test(type)) return false;
    // Viewport prefetch requires positive evidence of a fast connection.
    if (viewport) return type === '4g';
    return type === '4g' || (!type && config.allowUnknownConnection === true);
  }

  function active(viewport = false) {
    return !destroyed && !stopped && config.enabled === true &&
      !window.Shopify?.designMode && document.visibilityState === 'visible' &&
      mechanism !== 'none' && networkOK(viewport) &&
      allowedPath(localPath(location.pathname) || '');
  }

  function eligible(anchor) {
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.isConnected) return null;
    if (anchor.closest('[data-no-prefetch], [data-performance-ignore], [inert], form')) return null;
    if (anchor.hasAttribute('download') || anchor.hasAttribute('ping') ||
        anchor.hasAttribute('data-method') || anchor.hasAttribute('data-remote') ||
        anchor.hasAttribute('onclick') || anchor.getAttribute('role') === 'button' ||
        anchor.getAttribute('aria-disabled') === 'true') return null;
    const target = anchor.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target');
    if (target && target.toLowerCase() !== '_self') return null;
    if (/\b(external|nofollow)\b/i.test(anchor.getAttribute('rel') || '')) return null;
    const raw = (anchor.getAttribute('href') || '').trim();
    // Reject ANY fragment, even on another page, and encoded path separators.
    if (!raw || raw.includes('#')) return null;
    let url;
    try { url = new URL(raw, document.baseURI); } catch (_) { return null; }
    if (!/^https?:$/.test(url.protocol) || url.origin !== location.origin ||
        url.username || url.password || url.search || url.hash || /%(?:2f|5c|25)/i.test(url.pathname)) return null;
    let decoded;
    try { decoded = decodeURIComponent(url.pathname); } catch (_) { return null; }
    if (/[\\\s?#\x00-\x1f]/.test(decoded)) return null;
    if (!allowedPath(localPath(decoded) || '')) return null;
    if (url.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '')) return null;
    return url.href;
  }

  function prefetch(anchor, reason) {
    const viewport = reason === 'viewport';
    if (!active(viewport) || seen.size >= config.maxPrefetches ||
        (viewport && viewportCount >= config.maxViewportPrefetches)) return;
    const href = eligible(anchor); // Revalidate at dispatch: DOM/href may change.
    if (!href || seen.has(href)) return;
    const node = document.createElement(supportsRules ? 'script' : 'link');
    node.setAttribute('data-shopify-performance-layer', reason);
    if (supportsRules) {
      node.type = 'speculationrules';
      if (config.nonce) node.nonce = config.nonce;
      // One immutable list rule per selected URL. Intent/dwell already gated by
      // JS, so immediate is deliberate. No broad document rules bypass budgets.
      node.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [href], eagerness: 'immediate' }] });
    } else {
      node.rel = 'prefetch';
      node.as = 'document';
      node.href = href;
    }
    // Do not retry/fetch on errors: CSP, browser policy or HTTP cache headers
    // may forbid prefetch. A second transport could duplicate requests.
    try {
      document.head.appendChild(node);
      hints.add(node);
      seen.add(href);
      if (viewport) viewportCount++;
      if (config.debug) console.debug('[ShopifyPerformanceLayer] hint', reason, href);
    } catch (_) { node.remove(); }
  }

  function linkFrom(event) {
    const target = event.target;
    return target instanceof Element ? target.closest('a[href]') : null;
  }
  function clearHover() {
    clearTimeout(hoverTimer);
    hoverLink = null;
  }
  function onOver(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const anchor = linkFrom(event);
    if (!anchor || anchor === hoverLink) return;
    clearHover();
    if (!active() || !eligible(anchor)) return;
    hoverLink = anchor;
    hoverTimer = setTimeout(() => prefetch(anchor, 'hover'), config.hoverDelay);
  }
  function onOut(event) {
    if (hoverLink && !hoverLink.contains(event.relatedTarget)) clearHover();
  }
  function onTouch(event) {
    if (event.touches.length === 1) prefetch(linkFrom(event), 'touchstart');
  }
  function listen(target, name, fn, options) {
    target.addEventListener(name, fn, options);
    cleanups.push(() => target.removeEventListener(name, fn, options));
  }
  function clearPending() {
    clearHover();
    clearTimeout(scanTimer);
    viewportTimers.forEach(clearTimeout);
    viewportTimers.clear();
    hints.forEach(node => node.remove());
    hints.clear();
    // Removing hints cannot reliably cancel requests or evict browser caches.
    // Keep the budget spent; reconnect/visibility changes must not cause retries.
  }
  function scan() {
    if (!observer || destroyed || stopped) return;
    document.querySelectorAll(config.viewportSelector).forEach(anchor => {
      if (eligible(anchor)) observer.observe(anchor);
    });
  }
  function start() {
    if (started || destroyed) return;
    started = true;
    if (!config.enabled || window.Shopify?.designMode || mechanism === 'none') return;
    const pointer = 'PointerEvent' in window;
    listen(document, pointer ? 'pointerover' : 'mouseover', onOver, { passive: true });
    listen(document, pointer ? 'pointerout' : 'mouseout', onOut, { passive: true });
    listen(document, 'touchstart', onTouch, { passive: true });
    listen(document, 'visibilitychange', () => { if (!active()) clearPending(); });
    listen(window, 'offline', clearPending);
    listen(window, 'pagehide', clearPending);
    if (connection?.addEventListener) listen(connection, 'change', clearPending);
    // Integrators may pause after a cart/login/market mutation. We intentionally
    // do not patch fetch, forms, history, theme events or navigation.
    listen(document, 'shopify-performance:pause', pause);
    listen(document, 'shopify-performance:resume', resume);
    if (config.viewport === true && 'IntersectionObserver' in window) {
      try { document.querySelector(config.viewportSelector); } catch (_) { return; }
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const anchor = entry.target;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
            clearTimeout(viewportTimers.get(anchor));
            viewportTimers.delete(anchor);
          } else if (!viewportTimers.has(anchor) && active(true)) {
            viewportTimers.set(anchor, setTimeout(() => {
              viewportTimers.delete(anchor);
              prefetch(anchor, 'viewport');
              observer.unobserve(anchor);
            }, config.viewportDelay));
          }
        }
      }, { threshold: 0.5, rootMargin: '0px' });
      scan();
      // Observe added links in AJAX sections without relying on theme classes.
      mutations = new MutationObserver(records => {
        if (records.some(record => record.type === 'attributes' || record.addedNodes.length)) {
          clearTimeout(scanTimer);
          scanTimer = setTimeout(scan, 200);
        }
      });
      mutations.observe(document.body, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['href', 'data-performance-viewport'] });
      listen(document, 'shopify:section:load', scan);
    }
  }
  function pause() { stopped = true; clearPending(); observer?.disconnect(); }
  function resume() { if (!destroyed) { stopped = false; scan(); } }
  function destroy() {
    destroyed = true;
    clearPending();
    observer?.disconnect();
    mutations?.disconnect();
    cleanups.splice(0).forEach(remove => remove());
  }
  window.ShopifyPerformanceLayer = Object.freeze({
    version: '1.0.0', pause, resume, destroy,
    status: () => ({ mechanism, active: active(), paused: stopped, destroyed,
      hintsIssued: seen.size, viewportHintsIssued: viewportCount })
  });
  if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', start, { once: true });
  else start();
})();
